// Cookie consent state management.
// Stores user preferences in localStorage and exposes hooks/events
// for activating GA4 / Meta Pixel etc. only after consent.

const STORAGE_KEY = "c2_cookie_consent_v1";
const EVENT_NAME = "c2-consent-changed";

export const DEFAULT_CONSENT = {
  necessary: true, // always on
  analytics: false,
  marketing: false,
  timestamp: null,
  version: 1,
};

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // version check — invalidate older versions
    if (parsed?.version !== DEFAULT_CONSENT.version) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setConsent(partial) {
  const current = getConsent() || DEFAULT_CONSENT;
  const next = {
    ...current,
    ...partial,
    necessary: true,
    timestamp: new Date().toISOString(),
    version: DEFAULT_CONSENT.version,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  // Notify any listeners (e.g., tracker loaders)
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
  applyConsentToTrackers(next);
  return next;
}

export function clearConsent() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
}

export function onConsentChange(handler) {
  const listener = (e) => handler(e.detail);
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}

// ---- Tracker activation hooks ----
// Add real IDs in /app/frontend/.env (REACT_APP_GA4_ID, REACT_APP_META_PIXEL_ID)
// Trackers fire only when corresponding consent category is granted.

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

function loadGA4() {
  if (!GA4_ID || window.__ga4Loaded) return;
  window.__ga4Loaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { anonymize_ip: true });
}

function loadMetaPixel() {
  if (!META_PIXEL_ID || window.__metaPixelLoaded) return;
  window.__metaPixelLoaded = true;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
  window.fbq("track", "PageView");
}

export function applyConsentToTrackers(consent) {
  if (!consent) return;
  if (consent.analytics) loadGA4();
  if (consent.marketing) loadMetaPixel();
}

// Init on first load if user already consented previously
export function initConsentOnLoad() {
  const c = getConsent();
  if (c) applyConsentToTrackers(c);
}
